"use client";

import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { toBets, toGames, toLiveWins, toSportsbookBets, toTransfers, type SportsbookBet } from "./mappers";
import { platformFetch } from "./browser";
import { PAGE_LIMIT_MAX_100, clampLimit } from "./paginate";
import { useIdentity } from "./session";

/* Client-side reads. Everything goes through the /api/platform proxy — the
   API host refuses browser requests from our origin. */

type ApiGame = Parameters<typeof toGames>[0][number];
type GamesEnvelope = { data: ApiGame[]; total: number; nextCursor: string | null };

export const useDebounced = <T>(value: T, delay = 300) => {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);

    return () => clearTimeout(timer);
  }, [value, delay]);

  return debounced;
};

/* One hook for both browse modes: the whole catalogue, or a single category.
   `/games` has no category filter, so a category browse has to go through
   `/categories/{id}/games` instead — the caller should not have to know. */
export const useGameBrowse = ({
  categoryId,
  query,
  studioIds,
  limit = 24,
  enabled = true,
}: {
  categoryId?: string | null;
  query?: string;
  studioIds?: string[];
  limit?: number;
  enabled?: boolean;
}) => {
  const path = categoryId ? `categories/${categoryId}/games` : "games";

  const result = useInfiniteQuery({
    queryKey: ["games", categoryId ?? "all", query ?? "", studioIds ?? []],
    enabled,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      platformFetch<GamesEnvelope>(path, {
        limit: clampLimit(limit),
        query: query || undefined,
        studioIds: studioIds && studioIds.length > 0 ? studioIds : undefined,
        nextCursor: pageParam,
      }),
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });

  const pages = result.data?.pages ?? [];

  return {
    ...result,
    games: toGames(pages.flatMap((page) => page.data)),
    total: pages.at(0)?.total ?? 0,
  };
};

/* Favorites and recently played are ours, not the API's: the browser holds a
   list of game ids and each one still has to be read back from `/games/{id}`,
   which is why this reads one game per id rather than filtering a listing. A
   game that has since left the catalogue drops out instead of failing the lot. */
export const useGamesByIds = (ids: string[], { enabled = true } = {}) => {
  const result = useQuery({
    queryKey: ["games", "by-id", ids],
    enabled: enabled && ids.length > 0,
    queryFn: async () => {
      const games = await Promise.all(
        ids.map((id) =>
          platformFetch<{ data: ApiGame }>(`games/${id}`)
            .then((envelope) => envelope.data)
            .catch(() => null),
        ),
      );

      return games.filter((game): game is ApiGame => game !== null);
    },
  });

  return {
    ...result,
    games: result.data ? toGames(result.data) : [],
  };
};

/* Games by slug. `/games/{gameId}` accepts `slug:{slug}` as well as a UUID —
   the same lookup the game page makes in server.ts. This is how a free spin
   gets its game: the reward names it by provider code in
   `config.response.game_codes`, and a game's code is its slug on this
   platform. It is not by `siteGameId`, which is a site-game association id
   and answers 404 from this endpoint every time — the lookup by that id never
   named a single game. A code the catalogue no longer has drops out rather
   than failing the lot. */
export const useGamesBySlugs = (slugs: string[], { enabled = true } = {}) => {
  const result = useQuery({
    queryKey: ["games", "by-slug", slugs],
    enabled: enabled && slugs.length > 0,
    queryFn: async () => {
      const games = await Promise.all(
        slugs.map((slug) =>
          platformFetch<{ data: ApiGame }>(`games/slug:${slug}`)
            .then((envelope) => envelope.data)
            .catch(() => null),
        ),
      );

      return games.filter((game): game is ApiGame => game !== null);
    },
  });

  return {
    ...result,
    games: result.data ? toGames(result.data) : [],
  };
};

/* The live feeds. Polled rather than pushed: the realtime socket carries
   transaction events, not bet objects, so it can only ever be a hint to
   refetch. Five seconds matches how fast the strip visibly moves. */
export const useCasinoBets = ({
  limit = 20,
  refetchMs = 5000,
  accountId,
}: { limit?: number; refetchMs?: number | false; accountId?: string } = {}) => {
  /* One player's history is a different thing from the live feed: it is
     read once for a stats view, not polled to keep a strip moving. */
  const result = useQuery({
    queryKey: ["bets", "casino", limit, accountId ?? "all"],
    queryFn: () =>
      platformFetch<{ data: Parameters<typeof toBets>[0] }>("bets/casino", {
        limit: clampLimit(limit, PAGE_LIMIT_MAX_100),
        accountId,
      }),
    refetchInterval: accountId ? false : refetchMs,
    staleTime: accountId ? 30_000 : 0,
  });

  return {
    ...result,
    wins: result.data ? toLiveWins(result.data.data) : [],
    bets: result.data ? toBets(result.data.data, Date.now()) : [],
  };
};

/* The sportsbook's own feed. Same polling contract as the casino one, but the
   rows are betslips rather than rounds. */
export const useSportsbookBets = ({ limit = 20, refetchMs = 15000, initial = [] as SportsbookBet[] } = {}) => {
  const result = useQuery({
    queryKey: ["bets", "sportsbook", limit],
    queryFn: () =>
      platformFetch<{ data: Parameters<typeof toSportsbookBets>[0] }>("bets/sportsbook", {
        limit: clampLimit(limit, PAGE_LIMIT_MAX_100),
      }),
    refetchInterval: refetchMs,
    staleTime: 0,
  });

  return {
    ...result,
    bets: result.data ? toSportsbookBets(result.data.data, Date.now()) : initial,
  };
};

/* What the BetBy renderer needs to start: the brand it belongs to, and a
   session token when there is a session to bet with.

   This has to run in the browser. The token is minted against the platform
   session cookie, which only the proxy can present — a server render has no
   cookie to send and would always come back signed out. `/sportsbook` also
   geolocates the caller and answers 403 where BetBy is not licensed, so a
   failure here is a legitimate answer rather than an outage. */
export type SportsbookLaunch = { token: string | null; brandId: string };

export const useSportsbookLaunch = ({
  siteBankrollId,
  lang,
  oddsFormat,
}: {
  siteBankrollId: string;
  lang: string;
  oddsFormat: "DECIMAL" | "FRACTIONAL" | "AMERICAN";
}) => {
  const { isSignedIn, isPending: identityPending } = useIdentity();

  const query = useQuery({
    queryKey: ["sportsbook", siteBankrollId, lang, oddsFormat, isSignedIn],
    /* Waiting for /whoami avoids starting the renderer signed-out and then
       tearing it down a moment later when the token arrives. */
    enabled: siteBankrollId !== "" && !identityPending,
    queryFn: () =>
      platformFetch<{ data: SportsbookLaunch }>("sportsbook", {
        siteBankrollId,
        lang,
        oddsFormat,
      }),
    /* A 403 is geoblocking and a 400 is a bad request — neither improves on a
       second attempt, and both would otherwise retry three times. */
    retry: false,
    refetchOnWindowFocus: false,
  });

  /* BetBy calls this when its token expires or its session needs renewing. It
     must resolve to a fresh token, so it refetches rather than reading cache. */
  const refresh = useCallback(async () => {
    const refreshed = await query.refetch();

    if (refreshed.isError || !refreshed.data) {
      throw new Error("Could not refresh the sportsbook session.");
    }

    return refreshed.data.data;
  }, [query.refetch]);

  return { ...query, launch: query.data?.data ?? null, refresh };
};

type TransferRow = Parameters<typeof toTransfers>[0][number];

export const useTransactions = ({
  enabled = true,
  limit = 25,
  refetchMs = 5000,
  accountId,
}: { enabled?: boolean; limit?: number; refetchMs?: number | false; accountId?: string } = {}) => {
  const result = useQuery({
    queryKey: ["transactions", accountId ?? "live"],
    enabled,
    queryFn: () => platformFetch<{ data: TransferRow[] }>("transactions", { accountId }),
    refetchInterval: accountId ? false : refetchMs,
    staleTime: accountId ? 30_000 : 0,
  });

  return {
    ...result,
    transfers: result.data ? toTransfers(result.data.data, Date.now()).slice(0, limit) : [],
  };
};

export const useRewardTransactions = ({
  enabled = true,
  limit = 25,
  refetchMs = 5000,
  accountId,
}: { enabled?: boolean; limit?: number; refetchMs?: number | false; accountId?: string } = {}) => {
  const result = useQuery({
    queryKey: ["rewards", "transactions", limit, accountId ?? "all"],
    enabled,
    queryFn: () =>
      platformFetch<{ data: TransferRow[] }>("rewards/transactions", {
        limit: clampLimit(limit, PAGE_LIMIT_MAX_100),
        accountId,
      }),
    refetchInterval: accountId ? false : refetchMs,
    staleTime: accountId ? 30_000 : 0,
  });

  return {
    ...result,
    transfers: result.data ? toTransfers(result.data.data, Date.now()) : [],
  };
};

export const useRealLaunch = () =>
  useMutation({
    mutationFn: async (gameId: string) => {
      const bankrolls = await platformFetch<{ data: { id: string }[] }>("bankrolls");
      const bankrollId = bankrolls.data.at(0)?.id;

      const launch = await platformFetch<{ data: { url: string } }>(`games/${gameId}/url`, {
        mode: "real",
        ...(bankrollId ? { bankrollId } : {}),
      });

      return launch.data.url;
    },
  });
